import { DataTable } from "@/components/data-table/data-table";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { usePage } from "@/components/page/PageContext";
import { CreateTeamButton } from "@/components/playlist/CreateTeamButton";
import { UpdateTeamDialog } from "@/components/playlist/UpdateTeamDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useDataTable } from "@/hooks/use-data-table";
import { getTeams, removeTeam } from "@/queries/team";
import { useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Calendar, FileText, Trash2, User, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

interface Team {
  id: number;
  name: string;
  description: string;
  created_at: string;
  member_count: number;
  leader?: {
    id: number;
    email: string;
    name: string;
  };
}

export default function TeamsPage() {
  const { setTitles } = usePage();
  useEffect(() => {
    setTitles(["Teams"]);
  }, []);

  const { data: teams = [], isLoading, error } = getTeams();

  const columns = useMemo<ColumnDef<Team>[]>(
    () => [
      {
        id: "created_at",
        accessorKey: "created_at",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Created" />
        ),
        cell: ({ getValue }) => {
          const date = new Date(getValue() as string);
          return <span className="text-sm">{date.toLocaleDateString()}</span>;
        },
        meta: {
          label: "Creation Date",
          icon: Calendar,
        },
        enableSorting: true,
      },
      {
        id: "name",
        accessorKey: "name",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Team Name" />
        ),
        cell: ({ getValue }) => (
          <span className="font-semibold">{getValue() as string}</span>
        ),
        meta: {
          label: "Name",
        },
        enableSorting: true,
      },
      {
        id: "description",
        accessorKey: "description",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Description" />
        ),
        cell: ({ getValue }) => {
          const description = getValue() as string;
          return (
            <span className="text-sm max-w-xs truncate">
              {description || "No description"}
            </span>
          );
        },
        meta: {
          label: "Description",
          icon: FileText,
        },
        enableSorting: true,
      },
      {
        id: "member_count",
        accessorKey: "member_count",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Members" />
        ),
        cell: ({ getValue }) => (
          <span className="font-mono">{getValue() as number}</span>
        ),
        meta: {
          label: "Members",
          icon: Users,
        },
        enableSorting: true,
      },
      {
        id: "leader",
        accessorKey: "leader",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Leader" />
        ),
        cell: ({ getValue }) => {
          const leader = getValue() as Team["leader"];
          if (!leader) {
            return (
              <span className="text-sm text-gray-400">No leader assigned</span>
            );
          }
          return (
            <div className="flex flex-col">
              <span className="font-medium text-sm">{leader.name}</span>
              <span className="text-xs text-gray-500">{leader.email}</span>
            </div>
          );
        },
        meta: {
          label: "Leader",
          icon: User,
        },
        enableSorting: true,
        sortingFn: (rowA, rowB) => {
          const aLeader = rowA.original.leader;
          const bLeader = rowB.original.leader;
          if (!aLeader && !bLeader) return 0;
          if (!aLeader) return 1;
          if (!bLeader) return -1;
          return aLeader.name.localeCompare(bLeader.name);
        },
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const team = row.original;
          return (
            <div className="flex items-center gap-2">
              <UpdateTeamDialog
                team={{
                  id: team.id,
                  name: team.name,
                  description: team.description,
                  leader: team.leader,
                }}
              />
              <RemoveTeamButton team={team} />
            </div>
          );
        },
        enableSorting: false,
      },
    ],
    []
  );

  const { table } = useDataTable({
    data: teams,
    columns,
    pageCount: 1,
    initialState: {
      sorting: [{ id: "created_at", desc: true }],
    },
  });

  if (isLoading) {
    return (
      <div className="container mx-auto p-3 md:p-6">
        <h1 className="text-2xl font-bold mb-4">Teams</h1>
        <div className="flex items-center justify-center h-32">
          <span className="text-gray-500">Loading teams...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-3 md:p-6">
        <h1 className="text-2xl font-bold mb-4">Teams</h1>
        <div className="flex items-center justify-center h-32">
          <span className="text-red-500">Error loading teams</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <DataTable table={table} hidePagination cellClassName="py-3">
        <DataTableToolbar table={table}>
          <CreateTeamButton />
        </DataTableToolbar>
      </DataTable>
    </div>
  );
}

function RemoveTeamButton({ team }: { team: Team }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const removeTeamMutation = removeTeam();

  const handleRemoveTeam = async () => {
    try {
      await removeTeamMutation.mutateAsync({
        teamId: team.id,
      });

      // Invalidate and refetch teams and team members
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      queryClient.invalidateQueries({ queryKey: ["team_members"] });
      toast({
        title: "Success",
        description: `Team "${team.name}" has been deleted successfully`,
      });
      setIsOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to delete team",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          disabled={removeTeamMutation.isPending}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Team</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the team{" "}
            <strong>"{team.name}"</strong>? This will also remove all team
            members and cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleRemoveTeam}
            disabled={removeTeamMutation.isPending}
          >
            {removeTeamMutation.isPending ? "Deleting..." : "Delete Team"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
